import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  Trophy,
  Settings,
  ChevronRight,
  Search,
  Loader2,
  Save,
  AlertCircle,
  ArrowLeft,
  User,
  Filter,
  X,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
];

const ROLE_TYPES = [
  { value: 'auditor', label: 'Auditor' },
  { value: 'leadership', label: 'Leadership' },
];

function RoleBadge({ roles }) {
  if (!roles || roles.length === 0) return null;
  const display = roles.map(r => {
    if (r === 'senior_scheduler') return 'Sr. Scheduler';
    return r.charAt(0).toUpperCase() + r.slice(1);
  });
  return (
    <div className="flex gap-1 flex-wrap">
      {display.map((r, i) => (
        <Badge key={i} variant="outline" className="text-xs px-1.5 py-0">
          {r}
        </Badge>
      ))}
    </div>
  );
}

function UserInitials({ first, last }) {
  return (
    <div className="flex items-center justify-center h-9 w-9 rounded-full bg-slate-200 text-slate-600 text-sm font-medium flex-shrink-0">
      {first?.charAt(0)}{last?.charAt(0)}
    </div>
  );
}

// Debounce hook
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Individual user settings row ───────────────────────────────────────────
function UserSettingsRow({ row, canEdit, onSaved }) {
  const { toast } = useToast();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    incentive_applicable: row.incentive_applicable,
    employment_type: row.employment_type || '',
    incentive_role_type: row.incentive_role_type || '',
    monthly_incentive_limit: row.monthly_incentive_limit != null ? String(row.monthly_incentive_limit) : '',
  });

  // Sync if parent row changes (e.g., after save)
  useEffect(() => {
    setForm({
      incentive_applicable: row.incentive_applicable,
      employment_type: row.employment_type || '',
      incentive_role_type: row.incentive_role_type || '',
      monthly_incentive_limit: row.monthly_incentive_limit != null ? String(row.monthly_incentive_limit) : '',
    });
    setDirty(false);
  }, [row.user_id, row.updated_at]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        incentive_applicable: form.incentive_applicable,
        employment_type: form.employment_type || null,
        incentive_role_type: form.incentive_role_type || null,
        monthly_incentive_limit: form.monthly_incentive_limit !== '' ? parseFloat(form.monthly_incentive_limit) : null,
      };
      const res = await api.put(`/incentives/settings/${row.user_id}`, payload);
      setDirty(false);
      toast({ title: 'Saved', description: `Settings updated for ${row.first_name} ${row.last_name}.` });
      onSaved(res.data);
    } catch (err) {
      const msg = getApiError(err, 'Failed to save settings.');
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`grid grid-cols-1 gap-4 p-4 border-b border-slate-100 last:border-0 transition-colors ${dirty ? 'bg-amber-50/40' : ''}`}
      data-testid={`settings-row-${row.user_id}`}
    >
      {/* Desktop layout */}
      <div className="flex items-start gap-4">
        {/* User info */}
        <UserInitials first={row.first_name} last={row.last_name} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <span className="font-medium text-slate-900 text-sm">
              {row.first_name} {row.last_name}
            </span>
            <span className="text-xs text-slate-500">{row.email}</span>
            <RoleBadge roles={row.roles} />
          </div>

          {/* Settings fields */}
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
            {/* Incentive Applicable toggle */}
            <div className="flex flex-col gap-1.5 col-span-2 md:col-span-1">
              <Label className="text-xs text-slate-500">Incentive Eligible</Label>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.incentive_applicable}
                  onCheckedChange={v => handleChange('incentive_applicable', v)}
                  disabled={!canEdit}
                  data-testid={`applicable-toggle-${row.user_id}`}
                />
                <span className="text-sm text-slate-700">
                  {form.incentive_applicable ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            {/* Employment Type */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-slate-500">Employment Type</Label>
              <Select
                value={form.employment_type || '__none__'}
                onValueChange={v => handleChange('employment_type', v === '__none__' ? '' : v)}
                disabled={!canEdit}
              >
                <SelectTrigger className="h-8 text-sm" data-testid={`employment-type-${row.user_id}`}>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Not set —</SelectItem>
                  {EMPLOYMENT_TYPES.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Incentive Role Type */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-slate-500">Incentive Role</Label>
              <Select
                value={form.incentive_role_type || '__none__'}
                onValueChange={v => handleChange('incentive_role_type', v === '__none__' ? '' : v)}
                disabled={!canEdit}
              >
                <SelectTrigger className="h-8 text-sm" data-testid={`role-type-${row.user_id}`}>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Not set —</SelectItem>
                  {ROLE_TYPES.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Monthly Limit */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-slate-500">Monthly Limit (£)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                className="h-8 text-sm"
                value={form.monthly_incentive_limit}
                onChange={e => handleChange('monthly_incentive_limit', e.target.value)}
                disabled={!canEdit}
                placeholder="No limit"
                data-testid={`monthly-limit-${row.user_id}`}
              />
            </div>
          </div>

          {/* Footer: updated info + save */}
          <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
            {row.updated_at ? (
              <p className="text-xs text-slate-400">
                Last updated by {row.updated_by_name || '—'} on{' '}
                {new Date(row.updated_at).toLocaleDateString('en-GB', {
                  day: '2-digit', month: 'short', year: 'numeric',
                })}
              </p>
            ) : (
              <p className="text-xs text-slate-400">Not yet configured</p>
            )}
            {canEdit && dirty && (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="gap-1.5 h-7 text-xs"
                data-testid={`save-btn-${row.user_id}`}
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Save
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function IncentiveSettingsPage() {
  const navigate = useNavigate();
  const { isAdmin, isSeniorScheduler } = useAuth();
  const { toast } = useToast();

  const canEdit = isAdmin || isSeniorScheduler;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [filterApplicable, setFilterApplicable] = useState('all');
  const [filterEmployment, setFilterEmployment] = useState('all');
  const [filterRoleType, setFilterRoleType] = useState('all');
  const debouncedSearch = useDebounce(search, 300);

  const buildParams = useCallback(() => {
    const params = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (filterApplicable !== 'all') params.incentive_applicable = filterApplicable === 'yes';
    if (filterEmployment !== 'all') params.employment_type = filterEmployment;
    if (filterRoleType !== 'all') params.incentive_role_type = filterRoleType;
    return params;
  }, [debouncedSearch, filterApplicable, filterEmployment, filterRoleType]);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/incentives/settings', { params: buildParams() });
      setRows(res.data);
    } catch (err) {
      const msg = getApiError(err, 'Failed to load settings.');
      setError(msg);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [buildParams, toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleRowSaved = (updatedRow) => {
    setRows(prev => prev.map(r => r.user_id === updatedRow.user_id ? updatedRow : r));
  };

  const hasFilters = filterApplicable !== 'all' || filterEmployment !== 'all' || filterRoleType !== 'all' || search;

  const clearFilters = () => {
    setSearch('');
    setFilterApplicable('all');
    setFilterEmployment('all');
    setFilterRoleType('all');
  };

  const eligibleCount = rows.filter(r => r.incentive_applicable).length;

  return (
    <div className="space-y-6" data-testid="incentive-settings-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/incentives" className="hover:text-slate-700 flex items-center gap-1">
          <Trophy className="h-4 w-4" />
          Incentives
        </Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-slate-700 font-medium">Beneficiary Settings</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/incentives')}
            data-testid="back-to-incentives-btn"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-slate-500" />
              <h1 className="text-xl font-semibold text-slate-900">Beneficiary Settings</h1>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              Configure incentive eligibility for active internal staff
            </p>
          </div>
        </div>
        {!loading && rows.length > 0 && (
          <div className="text-right flex-shrink-0">
            <p className="text-2xl font-semibold text-slate-900">{eligibleCount}</p>
            <p className="text-xs text-slate-500">eligible of {rows.length}</p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-9 h-9"
            data-testid="settings-search"
          />
        </div>

        <Select value={filterApplicable} onValueChange={setFilterApplicable}>
          <SelectTrigger className="w-36 h-9" data-testid="filter-applicable">
            <SelectValue placeholder="Eligibility" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Eligibility</SelectItem>
            <SelectItem value="yes">Eligible</SelectItem>
            <SelectItem value="no">Not Eligible</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterEmployment} onValueChange={setFilterEmployment}>
          <SelectTrigger className="w-40 h-9" data-testid="filter-employment">
            <SelectValue placeholder="Employment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Employment</SelectItem>
            <SelectItem value="full_time">Full Time</SelectItem>
            <SelectItem value="part_time">Part Time</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterRoleType} onValueChange={setFilterRoleType}>
          <SelectTrigger className="w-36 h-9" data-testid="filter-role-type">
            <SelectValue placeholder="Role Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="auditor">Auditor</SelectItem>
            <SelectItem value="leadership">Leadership</SelectItem>
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1.5 h-9 text-slate-500">
            <X className="h-3.5 w-3.5" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-sm text-slate-600">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchSettings}>Retry</Button>
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-20 text-center">
            <div className="flex items-center justify-center h-14 w-14 rounded-full bg-slate-100">
              <User className="h-7 w-7 text-slate-400" />
            </div>
            <div>
              <p className="font-medium text-slate-700">No staff found</p>
              <p className="text-sm text-slate-500 mt-1">
                {hasFilters ? 'No users match the current filters.' : 'No active internal users found.'}
              </p>
            </div>
            {hasFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {rows.map(row => (
              <UserSettingsRow
                key={row.user_id}
                row={row}
                canEdit={canEdit}
                onSaved={handleRowSaved}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
